import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import productsData from '../../data/innerpages/product/products.json';

const ProductSearch = ({ lightMode = false }) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [results, setResults] = useState([]);
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Create a flattened list of all series for searching
  const searchableProducts = useMemo(() => {
    const list = [];
    productsData.categories.forEach(cat => {
      cat.products.forEach(product => {
        list.push({
          title: product.title,
          catId: cat.id,
          catName: cat.name,
          slug: product.title.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
          img: product.img
        });
      });
    });
    return list;
  }, []);

  useEffect(() => {
    if (query.trim().length > 1) {
      const filtered = searchableProducts.filter(p => 
        p.title.toLowerCase().includes(query.toLowerCase()) || 
        p.catName.toLowerCase().includes(query.toLowerCase())
      ).slice(0, 8); // Limit to 8 suggestions
      setResults(filtered);
    } else {
      setResults([]);
    }
  }, [query, searchableProducts]);

  // Close search when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsOpen(false);
        setIsFocused(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (catId, slug) => {
    setIsOpen(false);
    setQuery('');
    navigate(`/product/${catId}/${slug}`);
  };

  return (
    <div
      className={`product-search-wrapper ${isFocused ? 'focused' : ''} ${query ? 'active' : ''}`}
      ref={searchRef}
    >
      <div
        className={`product-search-inline ${isFocused ? 'focused' : ''} ${query ? 'expanded' : ''}`}
        role="search"
        onClick={() => {
          setIsOpen(true);
          setIsFocused(true);
          inputRef.current?.focus();
        }}
      >
        <i className={`la la-search icon ${lightMode ? 'text-dark' : 'text-white'}`} aria-hidden="true"></i>
        <input
          type="text"
          className={`product-search-input ${lightMode ? 'is-light' : 'is-dark'}`}
          placeholder="Search..."
          value={query}
          ref={inputRef}
          onFocus={() => {
            setIsFocused(true);
            setIsOpen(true);
          }}
          onBlur={() => {
            // Delay blur slightly to allow clicking suggestions
            setTimeout(() => setIsFocused(false), 200);
          }}
          onChange={(e) => {
            setQuery(e.target.value);
            if (!isOpen) setIsOpen(true);
          }}
        />
      </div>

      {isOpen && (results.length > 0 || (query.trim().length > 1 && results.length === 0)) && (
        <div className="search-container animate__animated animate__fadeInDown">
          {results.length > 0 && (
            <ul className="suggestions-list list-unstyled mb-0">
              {results.map((res, idx) => (
                <li
                  key={idx}
                  className="suggestion-item p-2 mb-1 rounded cursor-pointer"
                  onClick={() => handleSelect(res.catId, res.slug)}
                >
                  <div className="d-flex align-items-center">
                    <img
                      src={res.img}
                      alt=""
                      className="item-thumb me-3 rounded"
                      style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                    />
                    <div>
                      <h6 className="fsz-14 mb-0 text-dark">{res.title}</h6>
                      <span className="fsz-12 text-muted">{res.catName}</span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {query.trim().length > 1 && results.length === 0 && (
            <div className="p-3 text-center text-muted fsz-14">No products found</div>
          )}
        </div>
      )}

      <style>{`
        .product-search-wrapper {
          position: relative;
          z-index: 1000;
          margin-right: 20px;
        }
        .product-search-inline {
          position: relative;
          display: flex;
          align-items: center;
          gap: 8px;
          border-radius: 999px;
          padding: 8px 12px;
          border: 1px solid rgba(0, 0, 0, 0.12);
          background: ${lightMode ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.18)'};
          backdrop-filter: blur(10px);
          width: 52px;
          transition: width 260ms ease, border-color 260ms ease, background 260ms ease;
          overflow: hidden;
          cursor: pointer;
        }
        .product-search-wrapper.focused .product-search-inline,
        .product-search-wrapper.active .product-search-inline {
          width: 180px;
          border-color: rgba(255, 72, 31, 0.35);
          background: ${lightMode ? '#fff' : 'rgba(0, 0, 0, 0.25)'};
        }
        .product-search-inline .icon {
          font-size: 18px;
          opacity: ${lightMode ? '0.65' : '0.9'};
        }
        .product-search-input {
          border: 0;
          outline: none;
          background: transparent;
          width: 100%;
          color: ${lightMode ? '#000' : '#fff'};
          font-size: 13px;
          min-width: 0;
          opacity: 0;
          transition: opacity 160ms ease;
        }
        .product-search-wrapper.focused .product-search-input,
        .product-search-wrapper.active .product-search-input {
          opacity: 1;
        }
        .product-search-input::placeholder {
          color: ${lightMode ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.65)'};
        }
        .search-container {
          position: absolute;
          top: 100%;
          right: 0;
          width: 100%;
          min-width: 220px;
          max-width: 320px;
          background: #fff;
          padding: 12px;
          border-radius: 15px;
          box-shadow: 0 10px 30px rgba(0,0,0,0.1);
          margin-top: 10px;
          border: 1px solid rgba(0,0,0,0.08);
        }
        .suggestion-item {
          transition: all 0.2s ease;
        }
        .suggestion-item:hover {
          background: rgba(255, 102, 0, 0.08);
          transform: translateX(5px);
        }
        .suggestion-item:hover h6 {
          color: #ff481f !important;
        }

        @media (max-width: 991px) {
          .product-search-wrapper {
            margin-right: 10px;
          }
          .product-search-inline {
            width: 44px;
            padding: 8px 10px;
          }
          .product-search-wrapper.focused .product-search-inline,
          .product-search-wrapper.active .product-search-inline {
            width: 60px;
          }
          .search-container {
             position: fixed;
             top: 140px;
             left: 50%;
             right: auto;
             width: calc(100% - 20px);
             transform: translateX(-40%);
             max-width: 200px;
             min-width: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default ProductSearch;
